import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useToken } from '@/common/helper';
import { isZoomlogiSupport } from '@/common/utils/utils.js';
import { RootState, MenuItem, SettingsPermissions } from '../types';

const OPERATIONAL_LEG_VIEW_FEATURE = 'operational leg view';

const hasRenderablePermission = (item?: MenuItem): boolean =>
  Boolean(
    item?.permissions?.is_view ||
    item?.permissions?.is_create ||
    item?.permissions?.is_update
  );

export const hasOperationalLegViewPermission = (
  menuItems?: MenuItem[] | null
): boolean => {
  if (!Array.isArray(menuItems) || menuItems.length === 0) {
    return false;
  }

  const queue: MenuItem[] = [...menuItems];
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) continue;

    if (
      current?.name?.trim()?.toLowerCase() === OPERATIONAL_LEG_VIEW_FEATURE &&
      hasRenderablePermission(current)
    ) {
      return true;
    }

    if (Array.isArray(current.sub_menus) && current.sub_menus.length > 0) {
      queue.push(...current.sub_menus);
    }
  }

  return false;
};

export const isOperationalLegViewEnabled = (
  enableCustomerLeg: boolean | null | undefined,
  menuItems?: MenuItem[] | null
): boolean =>
  Boolean(enableCustomerLeg) && hasOperationalLegViewPermission(menuItems);

export const useShipmentPermissions = () => {
  const mainMenu = useSelector((state: RootState) => state.menu.items);
  const featurePermissions = useSelector(
    (state: RootState) => state.menu.features
  );
  const { decodedUserToken } = useToken();

  const companyName = decodedUserToken()?.company_name;
  const enableCustomerLeg = decodedUserToken()?.enablecustomerleg;
  const isSupportCompany = isZoomlogiSupport(companyName);

  const shipmentPermissions = mainMenu?.find(
    (items) => items?.name?.toLowerCase() === 'shipment'
  );
  const allPermissions = shipmentPermissions?.permissions;

  const permissions = useMemo<SettingsPermissions>(() => {
    const companyNameVal = companyName?.toLowerCase();
    if (companyNameVal === 'zoomlogi support') {
      return {
        locationsView: true,
        transportProvidersView: true,
        productsView: true,
      };
    }

    const defaultPermissions: SettingsPermissions = {
      locationsView: false,
      transportProvidersView: false,
      productsView: false,
    };

    const settingsMenu = mainMenu?.find(
      (menu: MenuItem) => menu?.name === 'Settings'
    );
    if (!settingsMenu || !settingsMenu.sub_menus) {
      return defaultPermissions;
    }

    const perms: SettingsPermissions = { ...defaultPermissions };

    settingsMenu.sub_menus.forEach((submenu) => {
      const name = submenu.name?.toLowerCase() || '';

      if (name.includes('location')) {
        perms.locationsView = submenu.permissions?.is_view ?? false;
      }
      if (name.includes('transport provider')) {
        perms.transportProvidersView = submenu.permissions?.is_view ?? false;
      }
      if (name.includes('product')) {
        perms.productsView = submenu.permissions?.is_view ?? false;
      }
    });

    return perms;
  }, [companyName, mainMenu]);

  const hasOperationalLegView = useMemo(() => {
    return isOperationalLegViewEnabled(enableCustomerLeg, featurePermissions);
  }, [enableCustomerLeg, featurePermissions]);

  return {
    allPermissions,
    permissions,
    isSupportCompany,
    hasOperationalLegView,
  };
};
